import { useState, useEffect, useRef } from 'react';
import { useUserContext } from '../contexts/UserContext';
import { useBranch } from '../contexts/BranchContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { Search, SlidersHorizontal, X, Filter, Calculator, History, BarChart3, ChevronDown, ChevronUp, CreditCard, Plus, Trash2, Link2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EmptyState from '../components/ui/EmptyState';
import CreatePurchaseModal from '../components/CreatePurchaseModal';
import EditPurchaseModal from '../components/EditPurchaseModal';
import ViewPurchaseModal from '../components/ViewPurchaseModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import ViewGymPurchaseModal, { type GymPurchase } from '../components/ViewGymPurchaseModal';
import EditGymPurchaseModal from '../components/EditGymPurchaseModal';
import CancelPurchaseDialog from '../components/CancelPurchaseDialog';
import PaymentLinkModal from '../components/commerce/PaymentLinkModal';
import { fetchLinkCapabilities } from '../components/commerce/paymentLinkApi';

interface MembershipPlan {
  id: string;
  name: string;
  description: string | null;
  durationMonths: number | null;
  price: number | null;
  isActive: boolean;
  isDefault: boolean;
}

interface MembershipPaymentRecord {
  id: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  discountAmount: number;
  discountPercent: number;
  taxAmount: number;
  taxPercent: number;
  notes: string | null;
  createdAt: string;
}

interface Purchase {
  id: string;
  memberId: string;
  gymId: string;
  planId: string;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  /** Read from the membership's dates by the list endpoint (as on the Members tab). */
  effectiveStatus?: string;
  purchaseDate: string;
  discountAmount: number | null;
  discountPercent: number | null;
  baseAmount: number | null;
  taxAmount: number | null;
  taxPercent: number | null;
  totalAmount: number | null;
  notes: string | null;
  invoiceId: string | null;
  paymentMethod: string | null;
  paymentStatus: string;
  paidAmount: number | null;
  pendingAmount: number | null;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  plan: MembershipPlan;
  member: {
    id: string;
    name: string | null;
    fullName: string | null;
    displayName: string | null;
    email: string | null;
    phoneNo: string | null;
    phoneNumber: string | null;
  };
  gym: {
    id: string;
    name: string;
    displayName: string;
    gstin: string | null;
    registeredAddress: string | null;
  };
  creator: {
    id: string;
    name: string | null;
    displayName: string | null;
  } | null;
  membershipPayments: MembershipPaymentRecord[];
}

function getEffectivePaymentStatus(purchase: { paymentStatus?: string | null; dueDate?: string | null; status?: string | null }): string {
  const ps = purchase.paymentStatus || 'paid';
  if ((ps === 'due' || ps === 'partial') && purchase.dueDate && new Date(purchase.dueDate) < new Date()) {
    return 'overdue';
  }
  return ps;
}

/**
 * The plan status a row shows: the list endpoint's `effectiveStatus` (read from
 * the plan's dates), null for a sale with no plan in it. Rows from before the
 * endpoint sent it fall back to the stored status.
 */
const planStatusOf = (row: { status: string | null; effectiveStatus?: string | null }) =>
  row.effectiveStatus === undefined ? row.status : row.effectiveStatus;

/** 'Active' covers paused plans (the plan is running); 'Cancelled' covers refunds. */
function matchesPlanFilter(filter: string, status: string | null | undefined): boolean {
  if (!filter) return true;
  if (filter === 'active') return status === 'active' || status === 'paused';
  if (filter === 'cancelled') return status === 'cancelled' || status === 'refund';
  return status === filter;
}

const PLAN_STATUS_LABEL: Record<string, string> = { refund: 'refunded', removed: 'plan removed', pending: 'awaiting payment' };

/** Nothing for a sale with no plan — its payment status says all there is. */
function PlanStatusLabel({ status }: { status: string | null | undefined }) {
  if (!status) return null;
  const tone = status === 'active' ? 'text-green-600' : status === 'paused' || status === 'upcoming' || status === 'pending' ? 'text-amber-600' : 'text-gray-400';
  return <span className={`text-[9px] font-bold uppercase tracking-widest ${tone}`}>{PLAN_STATUS_LABEL[status] ?? status}</span>;
}

function PaymentStatusBadge({ status, membershipStatus }: { status: string; membershipStatus?: string | null }) {
  const isCancelled = membershipStatus === 'cancelled';
  if (status === 'paid' || (isCancelled && (status === 'partial' || status === 'due'))) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-green-50 text-green-700 text-[9px] font-black uppercase tracking-widest border border-green-100">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>Paid
    </span>
  );
  if (status === 'overdue') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-100 text-red-800 text-[9px] font-black uppercase tracking-widest border border-red-200">
      <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>Overdue
    </span>
  );
  if (status === 'partial') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-widest border border-amber-100">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>Partial
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-50 text-red-700 text-[9px] font-black uppercase tracking-widest border border-red-100">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>Due
    </span>
  );
}

export default function PurchasesPage() {
  const { hasPermission, userContext, isOwner } = useUserContext();
  const { selectedBranchId, setSelectedBranchId } = useBranch();
  const [searchParams] = useSearchParams();
  const [branches, setBranches] = useState<Array<{ id: string; name: string; displayName: string | null }>>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'list' | 'analytics'>((searchParams.get('tab') as any) || 'list');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<Purchase | null>(null);
  const [viewingPurchase, setViewingPurchase] = useState<Purchase | null>(null);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [expandedPurchases, setExpandedPurchases] = useState<Set<string>>(new Set());
  const [recordPaymentPurchase, setRecordPaymentPurchase] = useState<Purchase | null>(null);
  const filterDrawerRef = useRef<HTMLDivElement>(null);
  // Gym-purchase (new system)
  const [gymPurchases, setGymPurchases] = useState<GymPurchase[]>([]);
  // Analytics count money taken, whatever the Plan filter says: every sale not
  // cancelled or refunded, fetched without the filter when one is set.
  const [allGymPurchases, setAllGymPurchases] = useState<GymPurchase[]>([]);
  const [viewingGymPurchase, setViewingGymPurchase] = useState<GymPurchase | null>(null);
  const [editingGymPurchase, setEditingGymPurchase] = useState<GymPurchase | null>(null);
  const [gymPurchaseEditTab, setGymPurchaseEditTab] = useState<'payment' | 'details'>('details');
  const [purchaseToCancel, setPurchaseToCancel] = useState<GymPurchase | null>(null);
  // Payment links for what is owed: offered per gym only once it collects
  // online, so a desk-only gym sees no change.
  const [linkPurchase, setLinkPurchase] = useState<GymPurchase | null>(null);
  const [onlineGyms, setOnlineGyms] = useState<Record<string, boolean>>({});

  // Draft states for filters
  const [draftStatusFilter, setDraftStatusFilter] = useState(statusFilter);
  const [draftPaymentStatusFilter, setDraftPaymentStatusFilter] = useState(paymentStatusFilter);
  const [draftBranchFilter, setDraftBranchFilter] = useState(selectedBranchId || '');

  const toggleExpanded = (id: string) => {
    setExpandedPurchases(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sync draft states when drawer opens
  useEffect(() => {
    if (showFiltersDrawer) {
      setDraftStatusFilter(statusFilter);
      setDraftPaymentStatusFilter(paymentStatusFilter);
      setDraftBranchFilter(selectedBranchId || '');
    }
  }, [showFiltersDrawer, statusFilter, paymentStatusFilter, selectedBranchId]);

  // Fetch branches for the branch filter (restricted to accessible for non-owners).
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${API_BASE_URL}/api/branches`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        const list = (json.data || json.branches || []).filter(
          (b: any) => (b.status ?? 'active') !== 'inactive'
        );
        const accessible = userContext?.accessibleBranches || [];
        setBranches(isOwner ? list : list.filter((b: any) => accessible.includes(b.id)));
      } catch {
        // ignore
      }
    })();
  }, [isOwner, userContext?.accessibleBranches]);

  // Close filter drawer on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterDrawerRef.current && !filterDrawerRef.current.contains(event.target as Node)) {
        setShowFiltersDrawer(false);
      }
    }
    if (showFiltersDrawer) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFiltersDrawer]);

  const activeFilterCount = [
    statusFilter !== '',
    paymentStatusFilter !== '',
    !!selectedBranchId
  ].filter(Boolean).length;

  const canManagePurchases = hasPermission('manage_members');

  useEffect(() => {
    if (!canManagePurchases) return;
    const owing = gymPurchases.filter(gp => gp.status !== 'cancelled' && Number(gp.pendingAmount) > 0 && gp.gym?.id);
    const unknown = [...new Set(owing.map(gp => gp.gym!.id))].filter(id => !(id in onlineGyms));
    if (unknown.length === 0) return;
    Promise.all(unknown.map(id => fetchLinkCapabilities(id).then(c => [id, c.onlinePayments] as const).catch(() => [id, false] as const)))
      .then(entries => setOnlineGyms(prev => ({ ...prev, ...Object.fromEntries(entries) })));
  }, [gymPurchases, canManagePurchases, onlineGyms]);
  const canViewPurchases = hasPermission('view_members');

  // Fetch purchases (both legacy and new gym-purchases)
  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No access token');
      const headers = { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' };

      const branchParam = selectedBranchId ? `&branchId=${selectedBranchId}` : '';
      const statusParam = statusFilter ? `&status=${statusFilter}` : '';
      const paymentStatusParam = paymentStatusFilter ? `&paymentStatus=${paymentStatusFilter}` : '';
      // Payment-status filters (due/overdue) need every matching row, not just the
      // most recently created ones — a gym-purchases list capped at the default
      // 200 silently drops older overdue purchases past that window.
      const gymLimit = paymentStatusFilter ? 2000 : 200;
      const [legacyRes, gymRes, allGymRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/purchases?_=1${branchParam}${paymentStatusParam}`, { headers }),
        fetch(`${API_BASE_URL}/api/gym-purchases?limit=${gymLimit}${branchParam}${statusParam}${paymentStatusParam}`, { headers }),
        statusFilter
          ? fetch(`${API_BASE_URL}/api/gym-purchases?limit=${gymLimit}${branchParam}${paymentStatusParam}`, { headers })
          : null,
      ]);

      if (legacyRes.ok) {
        const data = await legacyRes.json();
        setPurchases(data.data || []);
      } else {
        const err = await legacyRes.json();
        toast.error(err.error || 'Failed to load purchases');
      }

      if (gymRes.ok) {
        const data = await gymRes.json();
        setGymPurchases(data.data || []);
        if (!allGymRes) setAllGymPurchases(data.data || []);
      }
      if (allGymRes?.ok) {
        const data = await allGymRes.json();
        setAllGymPurchases(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to load purchases data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPurchase = (purchase: Purchase) => {
    setEditingPurchase(purchase);
    setShowEditModal(true);
  };

  const handleViewPurchase = (purchase: Purchase) => {
    setViewingPurchase(purchase);
    setShowViewModal(true);
  };

  const handleDeletePurchase = async (purchase: Purchase) => {
    if (!window.confirm(`Are you sure you want to delete this purchase? This action cannot be undone.`)) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Not authenticated');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/purchases/${purchase.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Purchase deleted successfully');
        fetchPurchases(); // Refresh the purchases list
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete purchase');
      }
    } catch (error) {
      console.error('Error deleting purchase:', error);
      toast.error('Failed to delete purchase');
    }
  };

  const handleDeleteGymPurchase = async (purchase: GymPurchase) => {
    setPurchaseToCancel(purchase);
  };

  const handlePurchaseUpdated = () => {
    fetchPurchases(); // Refresh the purchases list
  };

  // const handleGenerateInvoice = async (purchase: Purchase) => {
  //   try {
  //     const { data: { session } } = await supabase.auth.getSession();
  //     if (!session?.access_token) {
  //       toast.error('Not authenticated');
  //       return;
  //     }

  //     const response = await fetch(`http://localhost:3002/api/purchases/${purchase.id}/invoice`, {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${session.access_token}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });

  //     if (response.ok) {
  //       const blob = await response.blob();
  //       const url = window.URL.createObjectURL(blob);
  //       const a = document.createElement('a');
  //       a.href = url;
  //       a.download = `invoice-${purchase.invoiceNumber || purchase.id}.pdf`;
  //       document.body.appendChild(a);
  //       a.click();
  //       window.URL.revokeObjectURL(url);
  //       document.body.removeChild(a);
  //       toast.success('Invoice generated successfully');
  //     } else {
  //       const error = await response.json();
  //       toast.error(error.error || 'Failed to generate invoice');
  //     }
  //   } catch (error) {
  //     console.error('Error generating invoice:', error);
  //     toast.error('Failed to generate invoice');
  //   }
  // };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (searchInput === '' && searchTerm !== '') {
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [searchInput, searchTerm]);

  useEffect(() => {
    if (canViewPurchases) {
      fetchPurchases();
    }
  }, [canViewPurchases, selectedBranchId, statusFilter, paymentStatusFilter]);

  // Filter legacy purchases
  const filteredLegacyPurchases = purchases.filter(purchase => {
    const matchesSearch = !searchTerm ||
      (purchase.member.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (purchase.member.displayName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (purchase.member.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (purchase.plan.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (purchase.invoiceId?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = matchesPlanFilter(statusFilter, planStatusOf(purchase));
    const effectiveStatus = getEffectivePaymentStatus(purchase);
    const matchesPaymentStatus = !paymentStatusFilter ||
      (paymentStatusFilter === 'due'
        ? effectiveStatus === 'due' || effectiveStatus === 'partial' || effectiveStatus === 'overdue'
        : paymentStatusFilter === 'overdue'
          ? effectiveStatus === 'overdue'
          : purchase.paymentStatus === paymentStatusFilter);
    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  // Filter gym-purchases and tag them
  const filteredGymPurchases = gymPurchases.filter(gp => {
    const memberName = (gp.member.displayName || gp.member.fullName || '').toLowerCase();
    const lineNames = gp.lineItems.map(l => l.name.toLowerCase()).join(' ');
    const matchesSearch = !searchTerm ||
      memberName.includes(searchTerm.toLowerCase()) ||
      lineNames.includes(searchTerm.toLowerCase()) ||
      (gp.invoiceId?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = matchesPlanFilter(statusFilter, planStatusOf(gp));
    const effectiveStatus = (() => {
      const ps = gp.paymentStatus;
      if ((ps === 'due' || ps === 'partial') && gp.dueDate && new Date(gp.dueDate) < new Date()) return 'overdue';
      return ps;
    })();
    const matchesPaymentStatus = !paymentStatusFilter ||
      (paymentStatusFilter === 'due'
        ? effectiveStatus === 'due' || effectiveStatus === 'partial' || effectiveStatus === 'overdue'
        : paymentStatusFilter === 'overdue'
          ? effectiveStatus === 'overdue'
          : gp.paymentStatus === paymentStatusFilter);
    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  // Merge and sort all purchases by date desc
  type UnifiedItem =
    | { _type: 'legacy'; data: Purchase; sortDate: Date }
    | { _type: 'gym'; data: GymPurchase; sortDate: Date };

  const allPurchases: UnifiedItem[] = [
    ...filteredLegacyPurchases.map(p => ({ _type: 'legacy' as const, data: p, sortDate: new Date(p.purchaseDate || p.createdAt) })),
    ...filteredGymPurchases.map(p => ({ _type: 'gym' as const, data: p, sortDate: new Date(p.createdAt) }))
  ].sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime());

  // Pagination on merged list
  const totalPages = Math.ceil(allPurchases.length / itemsPerPage);
  const paginatedPurchases = allPurchases.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Analytics calculations — money taken, independent of the Plan filter.
  const revenueGymPurchases = allGymPurchases.filter(p => p.status !== 'cancelled' && p.status !== 'refund');
  const getPurchaseRevenue = (p: Purchase) => {
    // Ensure we always return a number
    if (p.totalAmount !== null && p.totalAmount !== undefined) {
      return Number(p.totalAmount) || 0;
    }
    // Calculate from plan price and discount
    const planPrice = Number(p.plan.price) || 0;
    const discountAmount = Number(p.discountAmount) || 0;
    const baseAmount = planPrice - discountAmount;

    // If tax is available, add it
    if (p.taxAmount !== null && p.taxAmount !== undefined) {
      return baseAmount + (Number(p.taxAmount) || 0);
    }

    return baseAmount;
  };

  const analytics = {
    todayRevenue: [
      ...purchases.filter(p => new Date(p.purchaseDate).toDateString() === new Date().toDateString()).map(p => getPurchaseRevenue(p)),
      ...revenueGymPurchases.filter(p => new Date(p.createdAt).toDateString() === new Date().toDateString()).map(p => Number(p.totalAmount))
    ].reduce((sum, rev) => sum + rev, 0),
    todayCount: purchases.filter(p => new Date(p.purchaseDate).toDateString() === new Date().toDateString()).length +
                revenueGymPurchases.filter(p => new Date(p.createdAt).toDateString() === new Date().toDateString()).length,
    totalRevenue: purchases.reduce((sum, p) => sum + getPurchaseRevenue(p), 0) +
                  revenueGymPurchases.reduce((sum, p) => sum + Number(p.totalAmount), 0),
    revenueByPlan: purchases.reduce((acc: any, p) => {
      const name = p.plan.name;
      const revenue = getPurchaseRevenue(p);
      acc[name] = (Number(acc[name]) || 0) + Number(revenue);
      return acc;
    }, revenueGymPurchases.reduce((acc: any, p) => {
      // For gym purchases, we can group by line item types or just "Catalog Items"
      p.lineItems.forEach(item => {
        const name = item.name;
        const rev = (Number(item.price) - Number(item.discount)) * Number(item.quantity) * (1 + Number(item.gstPercent)/100);
        acc[name] = (Number(acc[name]) || 0) + rev;
      });
      return acc;
    }, {})),
    recentDaily: Array.from({ length: 7 }).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toDateString();
      const legacyDay = purchases.filter(p => new Date(p.purchaseDate).toDateString() === dateStr);
      const gymDay = revenueGymPurchases.filter(p => new Date(p.createdAt).toDateString() === dateStr);
      const rev = legacyDay.reduce((sum, p) => sum + getPurchaseRevenue(p), 0) +
                  gymDay.reduce((sum, p) => sum + Number(p.totalAmount), 0);
      return {
        date: date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' }),
        revenue: rev,
        count: legacyDay.length + gymDay.length
      };
    }).reverse()
  };

  if (!canViewPurchases) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view purchases.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-indigo-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">Gym Purchases</h1>
          <p className="text-sm font-bold text-gray-400 mt-2 uppercase tracking-widest">Orders & Revenue Tracking</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 relative">
          <div className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-2xl shadow-inner border border-gray-200">
            <button
              onClick={() => setActiveTab('list')}
              className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'list'
                ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5'
                : 'text-gray-500 hover:text-gray-700 hover:bg-white/40'
                }`}
            >
              <History className="w-3.5 h-3.5" />
              History
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'analytics'
                ? 'bg-white text-blue-600 shadow-md ring-1 ring-black/5'
                : 'text-gray-500 hover:text-gray-700 hover:bg-white/40'
                }`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Analytics
            </button>
          </div>

          {canManagePurchases && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Purchase</span>
            </button>
          )}
        </div>
      </div>

      {activeTab === 'list' && (
        <div className="flex items-center justify-between gap-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search member, plan, or invoice..."
              className="block w-full pl-9 pr-10 py-2.5 border border-transparent rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all shadow-inner"
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors group"
              >
                <X className="h-4 w-4 group-hover:scale-110 transition-transform" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={handleSearch}
              className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all active:scale-95 border border-gray-100 shadow-sm sm:hidden"
            >
              <Search className="w-5 h-5" />
            </button>

            <button
              onClick={() => setShowFiltersDrawer(true)}
              className={`relative p-2.5 rounded-xl transition-all active:scale-95 border shadow-sm flex items-center gap-2 ${activeFilterCount > 0
                ? 'bg-blue-50 border-blue-200 text-blue-600'
                : 'bg-gray-50 border-gray-100 text-gray-700 hover:bg-gray-100'
                }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white ring-2 ring-white">
                  {activeFilterCount}
                </span>
              )}
              <span className="hidden md:inline text-sm font-semibold">Filters</span>
            </button>
          </div>
        </div>
      )}

      {/* Filter Drawer */}
      {showFiltersDrawer && (
        <div className="fixed inset-0 z-[60] flex justify-end overflow-hidden bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            ref={filterDrawerRef}
            className="w-full max-w-[320px] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                <Filter className="w-5 h-5 text-blue-600" />
                Filters
              </h3>
              <button
                onClick={() => setShowFiltersDrawer(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {/* Plan Filter */}
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Plan</label>
                <div className="flex flex-wrap gap-2">
                  {['', 'active', 'upcoming', 'expired', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setDraftStatusFilter(status)}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${draftStatusFilter === status
                        ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200'
                        : 'bg-white border-gray-100 text-gray-600 hover:border-blue-200 hover:text-blue-600'
                        }`}
                    >
                      {status === '' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Status Filter */}
              <div className="space-y-4">
                <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Payment Status</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: '', label: 'All' },
                    { value: 'paid', label: 'Paid' },
                    { value: 'due', label: 'Due' },
                    { value: 'overdue', label: 'Overdue' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setDraftPaymentStatusFilter(value)}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${draftPaymentStatusFilter === value
                        ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-200'
                        : 'bg-white border-gray-100 text-gray-600 hover:border-blue-200 hover:text-blue-600'
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Branch Filter */}
              {branches.length > 1 && (
                <div className="space-y-4">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Branch</label>
                  <select
                    value={draftBranchFilter}
                    onChange={(e) => setDraftBranchFilter(e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium text-gray-700"
                  >
                    <option value="">All branches</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 ml-1">Also switches the branch scope shown in the top bar.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex gap-3">
              <button
                onClick={() => {
                  setStatusFilter('');
                  setPaymentStatusFilter('');
                  setSelectedBranchId(null);
                  setDraftBranchFilter('');
                  setShowFiltersDrawer(false);
                  setCurrentPage(1);
                }}
                className="flex-1 px-4 py-3 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-black hover:bg-gray-50 transition-all active:scale-95"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setStatusFilter(draftStatusFilter);
                  setPaymentStatusFilter(draftPaymentStatusFilter);
                  setSelectedBranchId(draftBranchFilter || null);
                  setShowFiltersDrawer(false);
                  setCurrentPage(1);
                }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-black hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-100"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'list' ? (
        <>
          {/* Purchases List */}
          {paginatedPurchases.length === 0 ? (
            <EmptyState
              title="No purchases found"
              description="No purchases match your current filters."
            />
          ) : (
            <div className="space-y-3">
              {paginatedPurchases.map((item) => {
                if (item._type === 'gym') {
                  const gp = item.data;
                  const effectivePayStatus = (() => {
                    const ps = gp.paymentStatus;
                    if ((ps === 'due' || ps === 'partial') && gp.dueDate && new Date(gp.dueDate) < new Date()) return 'overdue';
                    return ps;
                  })();
                  const isCancelled = gp.status === 'cancelled';
                  const hasDueGym = !isCancelled && (effectivePayStatus === 'due' || effectivePayStatus === 'partial' || effectivePayStatus === 'overdue');
                  const isOverdueGym = effectivePayStatus === 'overdue' && !isCancelled;
                  const memberName = gp.member.displayName || gp.member.fullName || 'Unknown';
                  const itemSummary = gp.lineItems.length === 1
                    ? gp.lineItems[0].name
                    : `${gp.lineItems.length} items: ${gp.lineItems.map(l => l.name).join(', ')}`;

                  const payments = gp.payments || [];
                  const isExpanded = expandedPurchases.has(gp.id);
                  const canLink = canManagePurchases && hasDueGym && Number(gp.pendingAmount) > 0 && !!gp.gym?.id && !!onlineGyms[gp.gym.id];

                  return (
                    <div key={`gym-${gp.id}`} className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${isOverdueGym ? 'border-red-300' : hasDueGym ? 'border-amber-200' : 'border-gray-100 hover:border-indigo-200'}`}>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center p-4 gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-sm flex-shrink-0 shadow-inner">
                            {memberName.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-gray-900 truncate">{memberName}</h3>
                              <span className="px-1.5 py-0.5 text-[8px] font-black bg-indigo-50 text-indigo-600 border border-indigo-100 rounded uppercase tracking-widest shrink-0">Order</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                              <span className="text-xs text-gray-500 font-medium truncate max-w-[160px] sm:max-w-none">{itemSummary}</span>
                              <span className="w-1 h-1 rounded-full bg-gray-300 hidden sm:inline"></span>
                              <span className="text-xs font-bold text-indigo-600 tabular-nums">₹{Number(gp.totalAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                              {hasDueGym && Number(gp.pendingAmount) > 0 && (
                                <span className={`text-xs font-bold tabular-nums ${isOverdueGym ? 'text-red-600' : 'text-amber-600'}`}>
                                  · ₹{Number(gp.pendingAmount).toFixed(2)} {isOverdueGym ? 'overdue' : 'due'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <PaymentStatusBadge status={effectivePayStatus} membershipStatus={gp.status} />
                              <PlanStatusLabel status={planStatusOf(gp)} />
                              {hasDueGym && gp.dueDate && (
                                <span className={`text-[9px] font-bold ${isOverdueGym ? 'text-red-600' : 'text-amber-600'}`}>
                                  due {new Date(gp.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 bg-gray-50 sm:bg-transparent -mx-4 -mb-4 sm:m-0 p-3 sm:p-0 border-t sm:border-t-0 border-gray-100">
                          <div className="flex flex-col sm:mr-4">
                            <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Date</span>
                            <span className="text-[10px] text-gray-600 font-bold">
                              {new Date(gp.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {(payments.length > 0 || hasDueGym) && (
                              <button onClick={() => toggleExpanded(gp.id)}
                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-gray-100" title={isExpanded ? 'Collapse' : 'Show payments'}>
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            )}
                            {canManagePurchases && hasDueGym && (
                              <button onClick={() => { setEditingGymPurchase(gp); setGymPurchaseEditTab('payment'); }}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200 bg-indigo-50/50" title="Record Receipt">
                                <CreditCard className="w-4 h-4" />
                              </button>
                            )}
                            {canLink && (
                              <button onClick={() => setLinkPurchase(gp)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200 bg-indigo-50/50" title="Payment link">
                                <Link2 className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => setViewingGymPurchase(gp)}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-gray-100" title="View / Invoice">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {canManagePurchases && (
                                <button onClick={() => { setEditingGymPurchase(gp); setGymPurchaseEditTab('details'); }}
                                  className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-gray-100" title="Edit Meta">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                            )}
                            {canManagePurchases && !isCancelled && (
                                <button onClick={() => handleDeleteGymPurchase(gp)}
                                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-gray-100" title="Cancel/Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Payment History for Gym Purchases */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Transaction History</p>
                            <div className="flex gap-4 text-[10px] font-bold">
                              <span className="text-gray-500">Total: <span className="text-gray-900">₹{Number(gp.totalAmount).toFixed(2)}</span></span>
                              <span className="text-green-600">Paid: ₹{Number(gp.paidAmount).toFixed(2)}</span>
                              {hasDueGym && <span className="text-amber-600">Balance: ₹{Number(gp.pendingAmount).toFixed(2)}</span>}
                            </div>
                          </div>
                          {payments.length === 0 ? (
                            <p className="text-xs text-gray-400 italic py-2">No transaction receipts recorded yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {payments.map((payment: any, idx: number) => (
                                <div key={payment.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-gray-100">
                                  <div className="flex items-center gap-3">
                                    <span className="text-[9px] font-black text-gray-400 w-4 text-right">{idx + 1}</span>
                                    <div>
                                      <p className="text-xs font-bold text-gray-900">₹{Number(payment.amount).toFixed(2)}</p>
                                      <p className="text-[9px] text-gray-400 font-medium">
                                        {payment.paymentMethod} · {new Date(payment.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                      </p>
                                    </div>
                                  </div>
                                  {payment.notes && <p className="text-[9px] text-gray-400 italic text-right max-w-[150px] truncate">{payment.notes}</p>}
                                </div>
                              ))}
                            </div>
                          )}
                          {canManagePurchases && hasDueGym && (
                            <button
                              onClick={() => { setEditingGymPurchase(gp); setGymPurchaseEditTab('payment'); }}
                              className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-900 transition-all flex items-center justify-center gap-2"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              Add Payment Record · ₹{Number(gp.pendingAmount).toFixed(2)}
                            </button>
                          )}
                          {canLink && (
                            <button
                              onClick={() => setLinkPurchase(gp)}
                              className="w-full py-2.5 bg-white text-indigo-700 border border-indigo-200 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
                            >
                              <Link2 className="w-3.5 h-3.5" />
                              Payment link · ₹{Number(gp.pendingAmount).toFixed(2)}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                const purchase = item.data;
                const isExpanded = expandedPurchases.has(purchase.id);
                const effectivePayStatus = getEffectivePaymentStatus(purchase);
                const isCancelled = purchase.status === 'cancelled';
                const isRefund = purchase.status === 'refund';
                const hasDue = !isCancelled && !isRefund && (effectivePayStatus === 'due' || effectivePayStatus === 'partial' || effectivePayStatus === 'overdue');
                const isOverdue = effectivePayStatus === 'overdue' && !isCancelled && !isRefund;
                const payments = purchase.membershipPayments || [];
                return (
                  <div key={purchase.id} className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${isOverdue ? 'border-red-300' : hasDue ? 'border-amber-200' : 'border-gray-100 hover:border-blue-200'}`}>
                    {/* Main Row */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center p-4 gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0 shadow-inner">
                          {(purchase.member.displayName || purchase.member.name || 'M').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-bold text-gray-900 truncate">
                            {purchase.member.displayName || purchase.member.name || 'Unknown Member'}
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <span className="text-xs text-gray-500 font-medium truncate max-w-[120px] sm:max-w-none">{purchase.plan.name}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-300 hidden sm:inline"></span>
                            {isRefund ? (
                              <span className="text-xs font-bold text-gray-500">Refunded ₹{Number(purchase.paidAmount || purchase.totalAmount || 0).toFixed(2)}</span>
                            ) : isCancelled ? (
                              <span className="text-xs font-bold text-gray-500">Paid ₹{Number(purchase.paidAmount || purchase.totalAmount || 0).toFixed(2)}</span>
                            ) : (
                              <>
                                <span className="text-xs font-bold text-blue-600">₹{getPurchaseRevenue(purchase).toLocaleString()}</span>
                                {hasDue && purchase.pendingAmount && (
                                  <span className={`text-xs font-bold ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>· ₹{Number(purchase.pendingAmount).toFixed(2)} {isOverdue ? 'overdue' : 'due'}</span>
                                )}
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <PaymentStatusBadge status={effectivePayStatus} membershipStatus={purchase.status} />
                            <PlanStatusLabel status={planStatusOf(purchase)} />
                            {hasDue && purchase.dueDate && (
                              <span className={`text-[9px] font-bold ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                                due {new Date(purchase.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-2 bg-gray-50 sm:bg-transparent -mx-4 -mb-4 sm:m-0 p-3 sm:p-0 border-t sm:border-t-0 border-gray-100">
                        <div className="flex flex-col sm:mr-4">
                          <span className="text-[9px] uppercase tracking-wider text-gray-400 font-bold">Date</span>
                          <span className="text-[10px] text-gray-600 font-bold">
                            {new Date(purchase.purchaseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {(payments.length > 0 || hasDue) && (
                            <button
                              onClick={() => toggleExpanded(purchase.id)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-100"
                              title={isExpanded ? 'Collapse' : 'Show payments'}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          )}
                          {canManagePurchases && hasDue && (
                            <button
                              onClick={() => setRecordPaymentPurchase(purchase)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 bg-blue-50/50"
                              title="Record Payment"
                            >
                              <CreditCard className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewPurchase(purchase)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors border border-gray-100"
                            title="View"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {canManagePurchases && (
                            <>
                              <button
                                onClick={() => handleEditPurchase(purchase)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors border border-gray-100"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeletePurchase(purchase)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors border border-gray-100"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Payment History */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Payment History</p>
                          <div className="flex gap-4 text-[10px] font-bold">
                            <span className="text-gray-500">Total: <span className="text-gray-900">₹{Number(purchase.totalAmount || 0).toFixed(2)}</span></span>
                            <span className="text-green-600">Paid: ₹{Number(purchase.paidAmount || 0).toFixed(2)}</span>
                            {hasDue && <span className="text-amber-600">Pending: ₹{Number(purchase.pendingAmount || 0).toFixed(2)}</span>}
                          </div>
                        </div>
                        {payments.length === 0 ? (
                          <p className="text-xs text-gray-400 italic py-2">No payment receipts recorded yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {payments.map((payment: any, idx: number) => (
                              <div key={payment.id} className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 border border-gray-100">
                                <div className="flex items-center gap-3">
                                  <span className="text-[9px] font-black text-gray-400 w-4 text-right">{idx + 1}</span>
                                  <div>
                                    <p className="text-xs font-bold text-gray-900">₹{Number(payment.amount).toFixed(2)}</p>
                                    <p className="text-[9px] text-gray-400 font-medium">
                                      {payment.paymentMethod} · {new Date(payment.paymentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  {Number(payment.taxAmount) > 0 && (
                                    <p className="text-[9px] text-gray-400">GST: ₹{Number(payment.taxAmount).toFixed(2)}</p>
                                  )}
                                  {payment.notes && <p className="text-[9px] text-gray-400 italic">{payment.notes}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {canManagePurchases && hasDue && (
                          <button
                            onClick={() => setRecordPaymentPurchase(purchase)}
                            className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                          >
                            <CreditCard className="w-3.5 h-3.5" />
                            Record Payment · ₹{Number(purchase.pendingAmount || 0).toFixed(2)} pending
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-medium text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </>
      ) : (
        /* Analytics View - Premium Redesign */
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Daily Summary Card - Premium Chart */}
            <div className="lg:col-span-8 bg-white rounded-3xl p-8 border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
                <div>
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight">Revenue Dynamics</h3>
                  <p className="text-sm text-gray-500 font-bold uppercase tracking-widest mt-1">Last 7 Analytical Cycles</p>
                </div>
                <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                  <span className="text-xs font-black text-blue-700 uppercase tracking-widest">Real-time Data</span>
                </div>
              </div>

              <div className="overflow-x-auto scrollbar-hide -mx-2 sm:mx-0">
                <div className="flex items-end justify-between gap-4 h-64 min-w-[600px] sm:min-w-0 px-2 sm:px-0">
                  {analytics.recentDaily.map((day, i) => {
                    const revenues = analytics.recentDaily.map(d => Number(d.revenue) || 0);
                    const maxRevenue = Math.max(...revenues, 1);
                    const dayRevenue = Number(day.revenue) || 0;
                    const heightPercent = maxRevenue > 0
                      ? Math.max((dayRevenue / maxRevenue) * 100, dayRevenue > 0 ? 5 : 0)
                      : 0;

                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-4 group" style={{ height: '100%' }}>
                        <div className="w-full h-full bg-gray-50/50 rounded-2xl relative flex items-end justify-center overflow-hidden border border-gray-50/50">
                          {dayRevenue > 0 && (
                            <div
                              className="w-full bg-gradient-to-t from-blue-600 via-blue-500 to-indigo-400 rounded-t-xl transition-all duration-700 group-hover:brightness-110 relative shadow-lg shadow-blue-100"
                              style={{
                                height: `${heightPercent}%`,
                                minHeight: '8px'
                              }}
                            >
                              <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-black py-2 px-3 rounded-xl opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 whitespace-nowrap z-10 pointer-events-none shadow-xl border border-white/10">
                                ₹{day.revenue.toLocaleString()}
                              </div>
                              {heightPercent > 20 && (
                                <div className="absolute top-2 w-full flex justify-center opacity-20 group-hover:opacity-40 transition-opacity">
                                  <div className="w-1/2 h-0.5 bg-white rounded-full"></div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[10px] font-black text-gray-900 uppercase tracking-widest">{day.date.split(' ')[0]}</span>
                          <span className="text-[9px] font-bold text-gray-400 uppercase">{day.date.split(' ')[1]}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* EOD Quick Stats - Premium Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              <div className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-3xl p-8 text-white shadow-2xl shadow-blue-100 flex flex-col justify-between relative overflow-hidden group">
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                      <Calculator className="w-5 h-5 text-blue-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black tracking-tight">Today's Pulse</h3>
                      <p className="text-[10px] font-black text-blue-300 uppercase tracking-widest opacity-60">Cycle: {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="p-5 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 group-hover:bg-white/10 transition-colors">
                      <p className="text-[10px] font-black text-blue-200 uppercase tracking-widest mb-1">Gross Inflow</p>
                      <span className="text-3xl font-black tracking-tight">₹{analytics.todayRevenue.toLocaleString()}</span>
                    </div>
                    <div className="p-5 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 group-hover:bg-white/10 transition-colors">
                      <p className="text-[10px] font-black text-blue-200 uppercase tracking-widest mb-1">Total Admissions</p>
                      <span className="text-3xl font-black tracking-tight">{analytics.todayCount}</span>
                    </div>
                  </div>
                </div>

                {/* Decoration */}
                <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
              </div>

              <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm space-y-6">
                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                  Revenue by Plan
                </h4>
                <div className="space-y-4">
                  {Object.entries(analytics.revenueByPlan).length > 0 ? (
                    Object.entries(analytics.revenueByPlan).slice(0, 4).map(([name, revenue]: [string, any]) => (
                      <div key={name} className="flex items-center justify-between group">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-[150px]">{name}</span>
                          <div className="w-32 h-1 bg-gray-50 rounded-full mt-1.5 overflow-hidden">
                            <div className="h-full bg-blue-500/20 rounded-full group-hover:bg-blue-500/40 transition-all" style={{ width: '60%' }}></div>
                          </div>
                        </div>
                        <span className="text-sm font-black text-gray-900 tracking-tight">₹{revenue.toLocaleString()}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 font-bold italic text-center py-4">No data streams found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Purchase Modal */}
      <CreatePurchaseModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchPurchases();
        }}
      />

      {/* Edit Purchase Modal */}
      <EditPurchaseModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPurchase(null);
        }}
        purchase={editingPurchase}
        onPurchaseUpdated={handlePurchaseUpdated}
      />

      {/* View Purchase Modal */}
      <ViewPurchaseModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewingPurchase(null);
        }}
        purchase={viewingPurchase as any}
      />

      {/* Record Payment Modal */}
      {recordPaymentPurchase && (
        <RecordPaymentModal
          purchase={recordPaymentPurchase as any}
          onClose={() => setRecordPaymentPurchase(null)}
          onSuccess={() => {
            setRecordPaymentPurchase(null);
            fetchPurchases();
          }}
        />
      )}

      {/* View Gym Purchase Modal */}
      <ViewGymPurchaseModal
        isOpen={!!viewingGymPurchase}
        onClose={() => setViewingGymPurchase(null)}
        purchase={viewingGymPurchase}
      />

      {/* Edit Gym Purchase Modal */}
      <EditGymPurchaseModal
        isOpen={!!editingGymPurchase}
        onClose={() => setEditingGymPurchase(null)}
        onSuccess={fetchPurchases}
        purchase={editingGymPurchase}
        initialTab={gymPurchaseEditTab}
      />
        {linkPurchase?.gym?.id && (
          <PaymentLinkModal
            gymId={linkPurchase.gym.id}
            memberId={linkPurchase.member.id}
            purchaseId={linkPurchase.id}
            memberName={linkPurchase.member.displayName || linkPurchase.member.fullName}
            memberPhone={linkPurchase.member.phoneNumber}
            onClose={() => setLinkPurchase(null)}
          />
        )}
        {purchaseToCancel && (
          <CancelPurchaseDialog
            isOpen={!!purchaseToCancel}
            purchase={purchaseToCancel as any}
            onClose={() => setPurchaseToCancel(null)}
            onSuccess={() => {
              setPurchaseToCancel(null);
              fetchPurchases();
            }}
          />
        )}
    </div>
  );
}
